export type ModoBusqueda = 'basica' | 'avanzada'

export type CriterioAvanzado = {
  campo: string
  valor: string
}

export type DocumentoResultado = {
  id?: string
  tipoDeDocumento?: string
  claveDeSistema?: string
  estatus?: string
  publicado?: boolean
  capturista?: string
  nombreCapturista?: string
  fechaInicialDePublicacion?: string
  fechaFinalDePublicacion?: string
  datosDescriptivos?: Record<string, unknown>
  datosDeControl?: Record<string, unknown>
  datosAdministrativos?: {
    capturista?: string
    nombreCapturista?: string
    dbOrigen?: string
    numeroAnterior?: number
  }
  coleccionesRegistro_id?: {
    nombre?: string
  }
}

export type ResultadoBusquedaBasicaApi = {
  consulta?: string
  total?: number
  pagina?: number
  tamanio?: number
  resultados?: DocumentoResultado[]
}

export type LayoutResultado = 'hemerografico' | 'control' | 'analitico'

export const MENSAJE_E1 =
  'No se encontraron resultados, intente nuevamente modificando los criterios de búsqueda'

export const MENSAJE_E2 =
  'No fue posible realizar la búsqueda, contacte al administrador del sistema'

export const MENSAJE_E3 =
  'Por el momento no se puede visualizar el registro. Por favor, intente más tarde.'

export const MENSAJE_E4 =
  'Por el momento no se puede editar el registro. Por favor, intente más tarde.'

export const CRITERIOS_AVANZADA_KEY = 'busquedaAvanzadaCriterios'
export const TEXTO_BUSQUEDA_KEY = 'busquedaBasicaTexto'

export function valorATexto(valor: unknown): string {
  if (valor === undefined || valor === null) {
    return ''
  }
  if (Array.isArray(valor)) {
    return valor
      .map((item) => valorATexto(item))
      .filter(Boolean)
      .join('; ')
  }
  if (typeof valor === 'object') {
    const objeto = valor as Record<string, unknown>
    const preferido = objeto.a ?? objeto.t ?? objeto.nombre ?? objeto.valor ?? objeto.g
    if (preferido !== undefined && preferido !== null) {
      return String(preferido)
    }
    return Object.values(objeto)
      .map((item) => valorATexto(item))
      .filter(Boolean)
      .join('; ')
  }
  return String(valor).trim()
}

export function obtenerValorMarc(
  datos: Record<string, unknown> | undefined,
  etiqueta: string,
  subcampo?: string,
): string {
  if (!datos) {
    return ''
  }

  const plano = subcampo ? datos[`${etiqueta}${subcampo}`] : datos[etiqueta]
  if (plano !== undefined && plano !== null && typeof plano !== 'object') {
    return String(plano).trim()
  }

  const campo = datos[etiqueta]
  if (campo === undefined || campo === null) {
    return ''
  }

  if (Array.isArray(campo)) {
    return campo
      .map((item) => {
        if (typeof item === 'object' && item !== null && subcampo) {
          return valorATexto((item as Record<string, unknown>)[subcampo])
        }
        return valorATexto(item)
      })
      .filter(Boolean)
      .join('; ')
  }

  if (typeof campo === 'object') {
    const valores = campo as Record<string, unknown>
    if (subcampo) {
      return valorATexto(valores[subcampo])
    }
    return valorATexto(valores.a ?? valores.t ?? valores.valor ?? valores)
  }

  return String(campo).trim()
}

export function obtenerNumeroRegistro(documento: DocumentoResultado): string {
  const desdeMarc = obtenerValorMarc(documento.datosDescriptivos, '001')
  if (desdeMarc) {
    return desdeMarc
  }
  const plano = documento.datosDescriptivos?.['001']
  if (plano !== undefined && plano !== null) {
    return valorATexto(plano)
  }
  return documento.claveDeSistema ?? documento.id ?? ''
}

function normalizar(texto: string | undefined): string {
  return (texto ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

function esHemerografiaTematica(tipo: string): boolean {
  return tipo === 'registroanaliticohemerografiatematica' || tipo === 'hemerografiatematica'
}

function esControlHemerografico(tipo: string): boolean {
  return tipo === 'controlhemerografico' || tipo === 'controlemerografico'
}

function esRegistroHemerografico(tipo: string): boolean {
  return tipo === 'registrohemerografico' || tipo === 'registroshemerografico'
}

export function etiquetaTipoDocumento(tipo: string | undefined): string {
  const n = normalizar(tipo)
  if (esRegistroHemerografico(n)) {
    return 'Registro hemerográfico'
  }
  if (esControlHemerografico(n)) {
    return 'Registro de control hemerográfico'
  }
  if (n === 'registroanalitico') {
    return 'Registro analítico'
  }
  if (n === 'registroanaliticoindicepublicacion') {
    return 'Registro analítico de Índices de Publicación'
  }
  if (esHemerografiaTematica(n)) {
    return 'Registro Analítico de Hemerografía Temática'
  }
  return tipo?.trim() || 'Tipo de documento no identificado'
}

export function layoutDeDocumento(documento: DocumentoResultado): LayoutResultado {
  const n = normalizar(documento.tipoDeDocumento)
  if (esControlHemerografico(n)) {
    return 'control'
  }
  if (
    n === 'registroanaliticoindicepublicacion' ||
    esHemerografiaTematica(n) ||
    n.includes('analitic')
  ) {
    return 'analitico'
  }
  return 'hemerografico'
}

function textoFecha(valor: unknown): string {
  if (valor === undefined || valor === null || valor === '') {
    return ''
  }
  const texto = String(valor)
  const iso = texto.match(/^(\d{4}-\d{2}-\d{2})/)
  if (iso) {
    return iso[1]
  }
  const fecha = new Date(texto)
  if (Number.isNaN(fecha.getTime())) {
    return texto
  }
  const anio = fecha.getUTCFullYear()
  const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0')
  const dia = String(fecha.getUTCDate()).padStart(2, '0')
  return `${anio}-${mes}-${dia}`
}

export function formatearFechaArticulo(documento: DocumentoResultado): string {
  const inicio = textoFecha(documento.fechaInicialDePublicacion)
  const fin = textoFecha(documento.fechaFinalDePublicacion)
  if (!inicio && !fin) {
    return ''
  }
  if (!fin || inicio === fin) {
    return inicio || fin
  }
  return `${inicio} - ${fin}`
}

export function claseEstatus(estatus: string | undefined): string {
  const n = normalizar(estatus)
  if (n === 'publicado') {
    return 'bh-estatus-publicado'
  }
  if (n.includes('esperando revision')) {
    return 'bh-estatus-revisar'
  }
  if (n.includes('en revision') || n === 'revision') {
    return 'bh-estatus-revision'
  }
  return 'bh-estatus-guardado'
}

export function colorBadgeTipo(tipo: string | undefined): string {
  const n = normalizar(tipo)
  if (esControlHemerografico(n)) {
    return 'warning'
  }
  if (n.includes('analitic')) {
    return 'success'
  }
  return 'primary'
}

export function obtenerCapturistaId(documento: DocumentoResultado): string {
  return (
    documento.capturista ||
    documento.datosAdministrativos?.capturista ||
    ''
  ).trim()
}

export function formatearCriteriosAvanzados(criterios: CriterioAvanzado[]): string {
  return criterios
    .filter((c) => c.campo && c.valor)
    .map((c) => `"${c.campo}": "${c.valor}"`)
    .join(', ')
}
